<template>
  <div id="prevent-integrations">
    <v-card>
      <div class="pa-5">
        <v-row class="align-sm-center">
          <v-col cols="12">
            <v-card-title class="pa-0">Prevent Integrations</v-card-title>
          </v-col>
        </v-row>
      </div>

      <v-divider />

      <v-data-table
        :items="merchantItems"
        :headers="headers"
        :expanded.sync="expandedItems"
        :loading="loading"
        :items-per-page="-1"
        hide-default-footer
        class="prevent-integrations-table elevation-1"
        item-key="companyId"
        show-expand
        :single-expand="false"
        disable-sort
      >
        <template v-slot:item="{ item, expand, isExpanded }">
          <tr
            :key="`merchant-${item.companyId}`"
            class="prevent-integrations-table__merchant-row actionable"
            @click="expand(!isExpanded)"
          >
            <td class="prevent-integrations-table__expand-col">
              <v-icon :class="{ 'chevron-rotated': isExpanded }" size="24">
                {{ icons.mdiChevronDown }}
              </v-icon>
            </td>
            <td class="prevent-integrations-table__outer-equal-col">
              <span class="prevent-integrations-table__company-name">
                {{ item.companyName }}
              </span>
            </td>
            <td class="prevent-integrations-table__outer-equal-col">
              {{ item.integrationsCount }}
            </td>
            <td class="prevent-integrations-table__date-col">
              {{ formatDateTime(item.lastUpdatedAt) }}
            </td>
          </tr>
        </template>

        <template v-slot:expanded-item="{ item, headers: expandedHeaders }">
          <tr class="prevent-integrations-table__expanded-row">
            <td
              :colspan="expandedHeaders.length"
              class="prevent-integrations-table__expanded-cell"
            >
              <v-simple-table dense class="prevent-integrations-table__subtable">
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="prevent-integrations-table__equal-col">Type</th>
                      <th class="prevent-integrations-table__equal-col">Client ID</th>
                      <th class="prevent-integrations-table__equal-col">Enabled</th>
                      <th class="prevent-integrations-table__equal-col">Created</th>
                      <th class="prevent-integrations-table__date-col">Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="integration in item.integrations"
                      :key="`integration-${integration.id}`"
                    >
                      <td class="prevent-integrations-table__equal-col">
                        {{ serviceTypeLabel(integration) }}
                      </td>
                      <td
                        class="prevent-integrations-table__equal-col prevent-integrations-table__client-id-cell"
                      >
                        <template v-if="integration.clientIds.length">
                          <span
                            v-for="clientId in integration.clientIds"
                            :key="clientId"
                            class="prevent-integrations-table__client-id"
                          >{{ clientId }}</span>
                        </template>
                        <template v-else>-</template>
                      </td>
                      <td class="prevent-integrations-table__equal-col">
                        <v-icon v-if="integration.isEnabled" color="success" small>
                          {{ icons.mdiCheck }}
                        </v-icon>
                        <v-icon v-else color="error" small>
                          {{ icons.mdiClose }}
                        </v-icon>
                      </td>
                      <td class="prevent-integrations-table__equal-col">
                        {{ formatDateTime(integration.createdAt) }}
                      </td>
                      <td class="prevent-integrations-table__date-col">
                        {{ formatDateTime(integration.updatedAt) }}
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </td>
          </tr>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import usePreventIntegrations from '@/views/analytics/prevent-integrations/usePreventIntegrations';
import { mdiChevronDown, mdiCheck, mdiClose } from '@mdi/js';

export default {
  name: 'PreventIntegrationsView',
  setup() {
    const {
      loading,
      merchantItems,
      expandedItems,
      serviceTypeLabel,
      formatDateTime,
      fetchOverview,
    } = usePreventIntegrations();

    const headers = [
      // Declared explicitly: under table-layout: fixed the header row sizes the
      // columns, and Vuetify's auto-generated expand header carries width 1px.
      {
        text: '',
        value: 'data-table-expand',
        width: '56px',
        class: 'prevent-integrations-table__expand-col',
        cellClass: 'prevent-integrations-table__expand-col',
      },
      {
        text: 'Company name',
        value: 'companyName',
        class: 'prevent-integrations-table__outer-equal-col',
        cellClass: 'prevent-integrations-table__outer-equal-col',
      },
      {
        text: 'Integrations count',
        value: 'integrationsCount',
        class: 'prevent-integrations-table__outer-equal-col',
        cellClass: 'prevent-integrations-table__outer-equal-col',
      },
      {
        text: 'Last updated',
        value: 'lastUpdatedAt',
        class: 'prevent-integrations-table__date-col',
        cellClass: 'prevent-integrations-table__date-col',
      },
    ];

    return {
      loading,
      merchantItems,
      expandedItems,
      headers,
      serviceTypeLabel,
      formatDateTime,
      fetchOverview,
      icons: {
        mdiChevronDown,
        mdiCheck,
        mdiClose,
      },
    };
  },
};
</script>

<style lang="scss" scoped>
$column-expand-width: 56px;
$column-date-width: 200px;
$inner-equal-columns: 4;
$outer-equal-columns: 2;

.prevent-integrations-table {
  ::v-deep th {
    font-size: 12px;
    white-space: nowrap;
  }

  /**
   * Both tables size their columns from the width rules below rather than from
   * their content, so a long company name or client id wraps inside its own
   * column instead of pushing the later columns out of sight.
   */
  ::v-deep table {
    table-layout: fixed;
  }

  &__expand-col {
    width: $column-expand-width;
    min-width: $column-expand-width;
    box-sizing: border-box;
  }

  /**
   * Outer table: Company name and Integrations count split whatever the expand
   * and Last updated columns leave over, so the two stay equal at any width.
   */
  &__outer-equal-col {
    width: calc(
      (100% - #{$column-expand-width} - #{$column-date-width}) /
        #{$outer-equal-columns}
    );
    box-sizing: border-box;
    overflow-wrap: break-word;
  }

  /** The last column of both tables — equal widths keep the two lined up. */
  &__date-col {
    width: $column-date-width;
    min-width: $column-date-width;
    box-sizing: border-box;
  }

  &__company-name {
    font-weight: 500;
    display: block;
    text-transform: uppercase;
  }

  &__merchant-row {
    &.actionable {
      cursor: pointer;
    }

    > td {
      border-top: 1px solid rgba(94, 86, 105, 0.14);
    }
  }

  /**
   * Inner table: every column except the last splits the space the Updated
   * column leaves over. Updated keeps $column-date-width so it stays lined up
   * with the outer table's Last updated column.
   */
  &__equal-col {
    width: calc((100% - #{$column-date-width}) / #{$inner-equal-columns});
    box-sizing: border-box;
  }

  /**
   * Client ids are free-form and can run past 100 unbreakable characters.
   * Without a fixed layout plus break-all the column stretches the whole inner
   * table and pushes Enabled/Created/Updated out of sight.
   */
  &__client-id-cell {
    word-break: break-all;
  }

  &__client-id {
    display: block;
    line-height: 20px;
  }

  &__expanded-cell {
    padding: 0 !important;
  }

  &__subtable {
    background-color: #f9fafb;
  }

  ::v-deep .v-icon {
    transition: transform 0.3s ease;
  }
}

.theme--dark .prevent-integrations-table__subtable {
  background-color: rgba(231, 227, 252, 0.04);
}

.chevron-rotated {
  transform: rotate(-180deg);
}
</style>
